/**
 * Citation recovery: turns a reloaded thread's stored `rag_search` tool calls
 * back into the same sources the live stream would have produced.
 */

/** One stored RAG hit (services/ai-engine/src/controllers/schema.py). */
export interface EngineRagResult {
  source_uri: string;
  source_display_name?: string | null;
  page_span?: number[] | null;
  chunk_text?: string | null;
  score?: number | null;
}

/** A persisted tool call, as stored on an assistant message. */
export interface EngineToolCall {
  tool_name: string;
  args?: Record<string, unknown>;
  result?: EngineRagResult[] | string | null;
}

/** A cited source recovered from a persisted `rag_search` tool result. */
export interface ChatSource {
  id: string;
  title: string;
  /** "pp. 45–47"-style page locator, when known. */
  detail?: string;
  /** The retrieved passage, shown when the citation is opened. */
  text?: string;
  /** Cosine similarity 0–1, if known. */
  score?: number;
}

/** Mirror of the engine's `_format_page_span`: [first, last] → "p. 12" / "pp. 45–47". */
function formatPageSpan(span?: number[] | null): string | undefined {
  if (!span || span.length === 0) return undefined;
  const first = span[0];
  const last = span[span.length - 1];
  return first === last ? `p. ${first}` : `pp. ${first}–${last}`;
}

/** Recover cited sources from persisted rag_search tool results, deduped on
 * (title, page) to match the live stream's own dedup. */
export function sourcesFromToolCalls(toolCalls: EngineToolCall[] | null | undefined): ChatSource[] {
  const sources: ChatSource[] = [];
  const seen = new Set<string>();
  for (const tc of toolCalls ?? []) {
    if (!Array.isArray(tc.result)) continue;
    for (const r of tc.result) {
      const title = r.source_display_name || r.source_uri;
      const detail = formatPageSpan(r.page_span);
      const key = `${title}|${detail ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      sources.push({
        id: `src-${sources.length}`,
        title,
        detail,
        text: r.chunk_text ?? undefined,
        score: r.score ?? undefined,
      });
    }
  }
  return sources;
}
